import React, { useState, useEffect } from 'react';
import { 
  getAdminBankAccounts, 
  reviewBankAccount, 
  getAdminBankAccountCheque, 
  getAdminWithdrawalRequests, 
  getAdminWithdrawalRequest, 
  approveWithdrawalRequest, 
  rejectWithdrawalRequest, 
  transferWithdrawalRequest 
} from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { showSuccessToast, showErrorToast } from '../../utils/toast';
import { formatDateDDMMYYYY, formatDateTimeDDMMYYYY } from '../../utils/dateUtils';
import { 
  Building, 
  User, 
  CreditCard, 
  FileText, 
  Calendar, 
  Clock, 
  Check, 
  X, 
  Eye, 
  Search, 
  Download, 
  ArrowRightLeft,
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
  AlertTriangle
} from 'lucide-react';

const PAGE_SIZE = 10;

const STATUS_COLORS = {
  INITIATED: 'bg-blue-100 text-blue-800 border-blue-200',
  APPROVED: 'bg-amber-100 text-amber-800 border-amber-200',
  TRANSFERRED: 'bg-green-100 text-green-800 border-green-200',
  REJECTED: 'bg-red-100 text-red-800 border-red-200',
};

const BANK_STATUS_COLORS = {
  PENDING: 'bg-amber-100 text-amber-800 border-amber-200',
  APPROVED: 'bg-green-100 text-green-800 border-green-200',
  REJECTED: 'bg-red-100 text-red-800 border-red-200',
};

const Withdrawals = () => {
  const [activeTab, setActiveTab] = useState('requests');
  const [loading, setLoading] = useState(true);

  // Bank Accounts Data
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankFilterStatus, setBankFilterStatus] = useState('ALL');

  // Withdrawal Requests Data
  const [allRequests, setAllRequests] = useState([]);
  const [requests, setRequests] = useState([]);
  const [reqFilters, setReqFilters] = useState({
    merchantId: '',
    status: 'ALL',
    fromDate: '',
    toDate: '',
  });

  // Requests Pagination
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Modals & Dialogs States
  const [reviewingBank, setReviewingBank] = useState(null);
  const [bankReviewForm, setBankReviewForm] = useState({
    status: 'APPROVED',
    remarks: '',
  });

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailedRequest, setDetailedRequest] = useState(null);
  
  const [processingAction, setProcessingAction] = useState(null); // 'APPROVE', 'REJECT', 'TRANSFER'
  const [actionForm, setActionForm] = useState({
    charge: '0',
    rejectionReason: '',
    comment: '',
    utr: '',
    transferDate: new Date().toISOString().split('T')[0],
  });

  const [loadingAction, setLoadingAction] = useState(false);
  const [fetchingCheque, setFetchingCheque] = useState(false);

  useEffect(() => {
    loadData();
  }, [activeTab, bankFilterStatus]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'requests') {
        await fetchRequests();
      } else {
        await fetchBankAccounts();
      }
    } catch (err) {
      console.error("Error fetching admin withdrawals data:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBankAccounts = async () => {
    try {
      const statusArg = bankFilterStatus === 'ALL' ? '' : bankFilterStatus;
      const res = await getAdminBankAccounts(statusArg, 0, 1000);
      const data = res.data?.items || res.data || [];
      setBankAccounts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      showErrorToast("Failed to load bank accounts list");
    }
  };

  const fetchRequests = async () => {
    try {
      const params = {
        merchantId: reqFilters.merchantId,
        status: reqFilters.status === 'ALL' ? '' : reqFilters.status,
        fromDate: reqFilters.fromDate,
        toDate: reqFilters.toDate,
        page: 0,
        pageSize: 1000 // client side paging consistent with project standard
      };
      const res = await getAdminWithdrawalRequests(params);
      const data = res.data?.items || res.data || [];
      
      setAllRequests(data);
      setTotalPages(Math.ceil(data.length / PAGE_SIZE));
      setRequests(data.slice(0, PAGE_SIZE));
      setPage(0);
    } catch (err) {
      console.error(err);
      showErrorToast("Failed to load withdrawal requests list");
    }
  };

  const handleFilterRequests = (e) => {
    e.preventDefault();
    fetchRequests();
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    const start = newPage * PAGE_SIZE;
    setRequests(allRequests.slice(start, start + PAGE_SIZE));
  };

  // View / Download cheque
  const handleViewCheque = async (id, merchantId) => {
    setFetchingCheque(true);
    try {
      const response = await getAdminBankAccountCheque(id);
      const contentType = response.headers['content-type'] || 'image/png';
      const file = new Blob([response.data], { type: contentType });
      const fileURL = URL.createObjectURL(file);
      
      if (contentType.includes('image') || contentType.includes('pdf')) {
        window.open(fileURL, '_blank');
      } else {
        const link = document.createElement('a');
        link.href = fileURL;
        link.download = `cheque-${merchantId}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error('Failed to load payment proof:', err);
      showErrorToast('Failed to load cancelled cheque file');
    } finally {
      setFetchingCheque(false);
    }
  };

  // Submit Bank Account Review
  const handleBankReviewSubmit = async (e) => {
    e.preventDefault();
    if (!reviewingBank) return;

    if (bankReviewForm.status === 'REJECTED' && !bankReviewForm.remarks.trim()) {
      return showErrorToast("Rejection reason is required for rejection");
    }

    setLoadingAction(true);
    try {
      const actionVerb = bankReviewForm.status === 'APPROVED' ? 'APPROVE' : 'REJECT';
      await reviewBankAccount(reviewingBank.id, {
        action: actionVerb,
        comment: bankReviewForm.remarks,
        rejectionReason: bankReviewForm.status === 'REJECTED' ? bankReviewForm.remarks : '',
      });

      showSuccessToast(`Bank account successfully ${bankReviewForm.status.toLowerCase()}!`);
      setReviewingBank(null);
      setBankReviewForm({ status: 'APPROVED', remarks: '' });
      await fetchBankAccounts();
    } catch (err) {
      console.error(err);
      showErrorToast(err.response?.data?.message || "Failed to submit bank review");
    } finally {
      setLoadingAction(false);
    }
  };

  // View Single request details & history
  const handleViewRequestDetails = async (req) => {
    setSelectedRequest(req);
    try {
      const res = await getAdminWithdrawalRequest(req.id);
      setDetailedRequest(res.data || req);
    } catch (err) {
      console.error("Error fetching single request details:", err);
      setDetailedRequest(req);
    }
  };

  // Process Action Dialog (Approve, Reject, Transfer)
  const openActionDialog = (req, action) => {
    setSelectedRequest(req);
    setProcessingAction(action);
    setActionForm({
      charge: '0',
      rejectionReason: '',
      comment: '',
      utr: '',
      transferDate: new Date().toISOString().split('T')[0],
    });
  };

  const handleActionSubmit = async (e) => {
    e.preventDefault();
    if (!selectedRequest || !processingAction) return;

    setLoadingAction(true);
    try {
      if (processingAction === 'APPROVE') {
        const chargeVal = parseFloat(actionForm.charge);
        if (isNaN(chargeVal) || chargeVal < 0) {
          throw new Error("Invalid transfer charge amount");
        }
        await approveWithdrawalRequest(selectedRequest.id, { 
          charge: chargeVal,
          comment: actionForm.comment 
        });
        showSuccessToast("Withdrawal request approved successfully!");
      } 
      else if (processingAction === 'REJECT') {
        if (!actionForm.rejectionReason.trim()) {
          throw new Error("Rejection reason is required");
        }
        await rejectWithdrawalRequest(selectedRequest.id, { 
          rejectionReason: actionForm.rejectionReason,
          comment: actionForm.comment
        });
        showSuccessToast("Withdrawal request rejected and wallet reverted!");
      } 
      else if (processingAction === 'TRANSFER') {
        if (!actionForm.utr.trim()) {
          throw new Error("UTR / Reference number is required");
        }
        await transferWithdrawalRequest(selectedRequest.id, { 
          utr: actionForm.utr, 
          transferDate: actionForm.transferDate,
          comment: actionForm.comment
        });
        showSuccessToast("Withdrawal marked as transferred successfully!");
      }

      setProcessingAction(null);
      setSelectedRequest(null);
      await fetchRequests();
    } catch (err) {
      console.error(err);
      showErrorToast(err.message || err.response?.data?.message || "Failed to process request action");
    } finally {
      setLoadingAction(false);
    }
  };

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={(val) => { setActiveTab(val); setPage(0); }} className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-[500px] bg-slate-100/80 p-1.5 rounded-2xl mb-8 shadow-inner border border-slate-200/50 h-auto">
          <TabsTrigger value="requests" className="rounded-xl py-2.5 text-sm font-bold transition-all data-[state=active]:shadow-md data-[state=active]:bg-white data-[state=active]:text-indigo-700">Withdrawal Requests</TabsTrigger>
          <TabsTrigger value="banks" className="rounded-xl py-2.5 text-sm font-bold transition-all data-[state=active]:shadow-md data-[state=active]:bg-white data-[state=active]:text-indigo-700">Bank Account Reviews</TabsTrigger>
        </TabsList>

        {/* ── Tab: Withdrawal Requests ── */}
        <TabsContent value="requests" className="space-y-6 outline-none">
          <Card className="shadow-md border-slate-100">
            <CardHeader>
              <CardTitle>Withdrawal Requests Management</CardTitle>
              <CardDescription>Review, approve, and mark merchant withdrawals as transferred.</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Filters Form */}
              <form onSubmit={handleFilterRequests} className="flex flex-wrap gap-4 items-end mb-6 bg-slate-50 p-4 rounded-xl border border-slate-100">
                <div className="space-y-1.5 min-w-[150px]">
                  <Label htmlFor="merchantId" className="text-xs font-semibold text-slate-500 uppercase">Merchant ID</Label>
                  <Input
                    id="merchantId"
                    placeholder="Search by ID"
                    value={reqFilters.merchantId}
                    onChange={(e) => setReqFilters({ ...reqFilters, merchantId: e.target.value })}
                    className="border-slate-200 bg-white"
                  />
                </div>
                <div className="space-y-1.5 min-w-[150px]">
                  <Label htmlFor="reqStatus" className="text-xs font-semibold text-slate-500 uppercase">Status</Label>
                  <Select 
                    value={reqFilters.status} 
                    onValueChange={(val) => setReqFilters({ ...reqFilters, status: val })}
                  >
                    <SelectTrigger className="border-slate-200 bg-white">
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border border-slate-200">
                      <SelectItem value="ALL">All Statuses</SelectItem>
                      <SelectItem value="INITIATED">INITIATED</SelectItem>
                      <SelectItem value="APPROVED">APPROVED</SelectItem>
                      <SelectItem value="TRANSFERRED">TRANSFERRED</SelectItem>
                      <SelectItem value="REJECTED">REJECTED</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="fromDate" className="text-xs font-semibold text-slate-500 uppercase">From Date</Label>
                  <Input
                    id="fromDate"
                    type="date"
                    value={reqFilters.fromDate}
                    onChange={(e) => setReqFilters({ ...reqFilters, fromDate: e.target.value })}
                    className="border-slate-200 bg-white"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="toDate" className="text-xs font-semibold text-slate-500 uppercase">To Date</Label>
                  <Input
                    id="toDate"
                    type="date"
                    value={reqFilters.toDate}
                    onChange={(e) => setReqFilters({ ...reqFilters, toDate: e.target.value })}
                    className="border-slate-200 bg-white"
                  />
                </div>
                <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium">
                  <Search className="h-4 w-4 mr-1.5" /> Filter
                </Button>
                {(reqFilters.merchantId || reqFilters.status !== 'ALL' || reqFilters.fromDate || reqFilters.toDate) && (
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => {
                      setReqFilters({ merchantId: '', status: 'ALL', fromDate: '', toDate: '' });
                      setTimeout(() => fetchRequests());
                    }}
                    className="border-slate-200"
                  >
                    Reset
                  </Button>
                )}
              </form>

              {/* Table list */}
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
                </div>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-slate-100">
                  <table className="w-full min-w-[850px] text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase">
                        <th className="p-4">Request / Date</th>
                        <th className="p-4">Merchant</th>
                        <th className="p-4">Amounts (Req / Net)</th>
                        <th className="p-4">Bank Destination</th>
                        <th className="p-4">Status</th>
                        <th className="p-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-sm">
                      {requests.map((req) => (
                        <tr key={req.id} className="hover:bg-slate-50 transition-colors">
                          <td className="p-4">
                            <div className="font-semibold text-gray-800">{req.requestId}</div>
                            <div className="text-xs text-gray-400 mt-0.5">{formatDateTimeDDMMYYYY(req.createdAt)}</div>
                          </td>
                          <td className="p-4">
                            <div className="font-semibold text-gray-800">{req.merchantName}</div>
                            <div className="text-xs text-gray-500 font-mono mt-0.5">{req.merchantId}</div>
                          </td>
                          <td className="p-4">
                            <div className="font-bold text-gray-800">Req: ₹{req.amount?.toLocaleString('en-IN')}</div>
                            <div className="text-xs text-indigo-600 font-bold mt-0.5">Net: ₹{req.netTransferAmount?.toLocaleString('en-IN')} (Chg: ₹{req.charge || 0})</div>
                          </td>
                          <td className="p-4">
                            <div className="font-semibold text-gray-700">{req.bankName}</div>
                            <div className="text-xs text-gray-400 font-mono mt-0.5">A/C: {req.accountNumber} • IFSC: {req.ifscCode}</div>
                          </td>
                          <td className="p-4">
                            <Badge className={`px-2.5 py-0.5 border text-xs font-semibold uppercase rounded-full ${STATUS_COLORS[req.status] || ''}`}>
                              {req.status}
                            </Badge>
                          </td>
                          <td className="p-4 text-right">
                            <div className="flex justify-end gap-1.5 flex-wrap">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 font-semibold"
                                onClick={() => handleViewRequestDetails(req)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>

                              {req.status === 'INITIATED' && (
                                <>
                                  <Button 
                                    size="sm" 
                                    className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white font-bold flex items-center gap-1.5 shadow-md shadow-emerald-500/30 rounded-lg transform transition-all duration-200 hover:-translate-y-0.5 px-3"
                                    onClick={() => openActionDialog(req, 'APPROVE')}
                                  >
                                    <Check className="h-3.5 w-3.5" /> Approve
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    className="bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-400 hover:to-red-500 text-white font-bold flex items-center gap-1.5 shadow-md shadow-red-500/30 rounded-lg transform transition-all duration-200 hover:-translate-y-0.5 px-3 border-none"
                                    onClick={() => openActionDialog(req, 'REJECT')}
                                  >
                                    <X className="h-3.5 w-3.5" /> Reject
                                  </Button>
                                </>
                              )}

                              {req.status === 'APPROVED' && (
                                <>
                                  <Button 
                                    size="sm" 
                                    className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold flex items-center gap-1.5 shadow-md shadow-blue-500/30 rounded-lg transform transition-all duration-200 hover:-translate-y-0.5 px-3"
                                    onClick={() => openActionDialog(req, 'TRANSFER')}
                                  >
                                    <ArrowRightLeft className="h-3.5 w-3.5" /> Transfer
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    className="bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-400 hover:to-red-500 text-white font-bold flex items-center gap-1.5 shadow-md shadow-red-500/30 rounded-lg transform transition-all duration-200 hover:-translate-y-0.5 px-3 border-none"
                                    onClick={() => openActionDialog(req, 'REJECT')}
                                  >
                                    <X className="h-3.5 w-3.5" /> Reject
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                      {requests.length === 0 && (
                        <tr>
                          <td colSpan="6" className="p-8 text-center text-gray-400">
                            No withdrawal requests found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-between items-center mt-6">
                  <span className="text-xs text-slate-500 font-medium">
                    Showing Page {page + 1} of {totalPages}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 0}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page === totalPages - 1}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab: Bank Account Reviews ── */}
        <TabsContent value="banks" className="space-y-6 outline-none">
          <Card className="shadow-md border-slate-100">
            <CardHeader className="flex flex-row items-center justify-between border-b border-slate-50 pb-4">
              <div>
                <CardTitle>Merchant Bank Accounts Review</CardTitle>
                <CardDescription>Verify and approve payout bank account requests from merchants.</CardDescription>
              </div>
              <div className="flex gap-2 items-center">
                <span className="text-xs font-semibold text-slate-400">Filter:</span>
                <Select 
                  value={bankFilterStatus} 
                  onValueChange={(val) => setBankFilterStatus(val)}
                >
                  <SelectTrigger className="border-slate-200 bg-white h-9 text-xs">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-slate-200 text-xs">
                    <SelectItem value="ALL">All Statuses</SelectItem>
                    <SelectItem value="PENDING">PENDING</SelectItem>
                    <SelectItem value="APPROVED">APPROVED</SelectItem>
                    <SelectItem value="REJECTED">REJECTED</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
                </div>
              ) : (
                <div className="flex flex-col gap-6">
                  {(Array.isArray(bankAccounts) ? bankAccounts : []).map((acct) => (
                    <Card key={acct.id} className="border border-slate-100 shadow-sm relative overflow-hidden">
                      <div className="p-5 space-y-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-bold text-gray-900">{acct.merchantName}</h4>
                            <span className="text-xs font-mono text-gray-400">{acct.merchantId}</span>
                          </div>
                          <Badge className={`px-2.5 py-0.5 border text-[10px] font-semibold uppercase rounded-full ${BANK_STATUS_COLORS[acct.status] || ''}`}>
                            {acct.status}
                          </Badge>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-4 gap-x-6 text-xs border-t border-slate-50 pt-4">
                          <div>
                            <span className="text-slate-400 block font-medium">Holder Name</span>
                            <span className="text-gray-800 font-semibold mt-0.5 block">{acct.accountHolderName || '—'}</span>
                          </div>
                          <div>
                            <span className="text-slate-400 block font-medium">Bank Name</span>
                            <span className="text-gray-800 font-semibold mt-0.5 block">{acct.bankName}</span>
                          </div>
                          <div>
                            <span className="text-slate-400 block font-medium">Account Number</span>
                            <span className="text-gray-800 font-mono font-bold mt-0.5 block">{acct.accountNumber}</span>
                          </div>
                          <div>
                            <span className="text-slate-400 block font-medium">IFSC / Branch</span>
                            <span className="text-gray-800 font-bold mt-0.5 block">{acct.ifscCode} <span className="font-normal text-gray-500">({acct.branchName || '—'})</span></span>
                          </div>
                        </div>

                        {acct.rejectionReason && (
                          <div className="p-2.5 bg-red-50 text-red-800 rounded-lg text-xs">
                            <strong>Reason:</strong> {acct.rejectionReason}
                          </div>
                        )}
                        {acct.adminComment && !acct.rejectionReason && (
                          <div className="p-2.5 bg-slate-50 text-slate-700 rounded-lg text-xs">
                            <strong>Note:</strong> {acct.adminComment}
                          </div>
                        )}

                        <div className="flex flex-col sm:flex-row gap-3 border-t border-slate-50 pt-4 mt-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="flex-1 bg-white hover:bg-slate-50 text-indigo-700 border-indigo-200 hover:border-indigo-300 font-bold shadow-sm transition-all duration-200 hover:-translate-y-0.5 rounded-lg"
                            onClick={() => handleViewCheque(acct.id, acct.merchantId)}
                            disabled={fetchingCheque}
                          >
                            <Download className="h-4 w-4 mr-1.5 text-indigo-500" /> Cheque Proof
                          </Button>
                          
                          {acct.status === 'PENDING' && (
                            <Button 
                              size="sm" 
                              className="flex-1 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white font-bold shadow-md shadow-indigo-500/30 transition-all duration-200 hover:-translate-y-0.5 rounded-lg border-none"
                              onClick={() => { setReviewingBank(acct); setBankReviewForm({ status: 'APPROVED', remarks: '' }); }}
                            >
                              Review & Action
                            </Button>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                  {(Array.isArray(bankAccounts) ? bankAccounts : []).length === 0 && (
                    <div className="w-full py-12 text-center text-gray-400 text-sm">
                      No bank accounts pending verification for the selected filter.
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── Dialog: Review Bank Account ── */}
      <Dialog open={!!reviewingBank} onOpenChange={() => setReviewingBank(null)}>
        <DialogContent className="max-w-md bg-white rounded-xl shadow-lg border border-slate-100">
          <DialogHeader>
            <DialogTitle>Review Merchant Bank Account</DialogTitle>
            <DialogDescription>Submit status decision for {reviewingBank?.merchantName}'s bank credentials.</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleBankReviewSubmit} className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold">Verification Action</Label>
              <Select 
                value={bankReviewForm.status} 
                onValueChange={(val) => setBankReviewForm({ ...bankReviewForm, status: val })}
              >
                <SelectTrigger className="border-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border border-slate-200">
                  <SelectItem value="APPROVED">Approve (Enable Payouts)</SelectItem>
                  <SelectItem value="REJECTED">Reject Submission</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="remarks" className="text-sm font-semibold">Review Comment / Rejection Reason</Label>
              <Textarea 
                id="remarks"
                placeholder={bankReviewForm.status === 'REJECTED' ? "Provide reason for rejection (required)" : "Admin feedback / internal comments"}
                value={bankReviewForm.remarks}
                onChange={(e) => setBankReviewForm({ ...bankReviewForm, remarks: e.target.value })}
                className="border-slate-200 min-h-[100px]"
                required={bankReviewForm.status === 'REJECTED'}
              />
            </div>

            <DialogFooter className="mt-4 flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setReviewingBank(null)} className="border-slate-200">Cancel</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold" disabled={loadingAction}>
                {loadingAction ? "Processing..." : "Confirm Review"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Dialog: Process Withdrawal Request Action (Approve / Reject / Transfer) ── */}
      <Dialog open={!!processingAction} onOpenChange={() => setProcessingAction(null)}>
        <DialogContent className="max-w-md bg-white rounded-xl shadow-lg border border-slate-100">
          <DialogHeader>
            <DialogTitle>
              {processingAction === 'APPROVE' && 'Approve Withdrawal Request'}
              {processingAction === 'REJECT' && 'Reject Withdrawal Request'}
              {processingAction === 'TRANSFER' && 'Mark Withdrawal as Transferred'}
            </DialogTitle>
            <DialogDescription>
              {processingAction === 'APPROVE' && `Enter transfer charge if applicable for ₹${selectedRequest?.amount?.toLocaleString()}`}
              {processingAction === 'REJECT' && 'Reject this request. The wallet balance will be automatically refunded.'}
              {processingAction === 'TRANSFER' && 'Provide reference details for the manual bank transfer.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleActionSubmit} className="space-y-4 pt-2">
            {processingAction === 'APPROVE' && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="charge" className="text-sm font-semibold">Transfer Charge (₹)</Label>
                  <Input
                    id="charge"
                    type="number"
                    placeholder="0.00"
                    min="0"
                    step="any"
                    value={actionForm.charge}
                    onChange={(e) => setActionForm({ ...actionForm, charge: e.target.value })}
                    className="border-slate-200"
                  />
                  <span className="text-xs text-gray-400">Net payout will be ₹{(parseFloat(selectedRequest?.amount || 0) - parseFloat(actionForm.charge || 0) || 0).toLocaleString()}</span>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="comment" className="text-sm font-semibold">Approval Remarks / Comment</Label>
                  <Textarea
                    id="comment"
                    placeholder="Provide any additional comments..."
                    value={actionForm.comment}
                    onChange={(e) => setActionForm({ ...actionForm, comment: e.target.value })}
                    className="border-slate-200 min-h-[70px]"
                  />
                </div>
              </>
            )}

            {processingAction === 'REJECT' && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="rejectionReason" className="text-sm font-semibold">Rejection Reason *</Label>
                  <Textarea
                    id="rejectionReason"
                    placeholder="Explain why this request is rejected..."
                    value={actionForm.rejectionReason}
                    onChange={(e) => setActionForm({ ...actionForm, rejectionReason: e.target.value })}
                    className="border-slate-200 min-h-[90px]"
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="comment" className="text-sm font-semibold">Additional Comments</Label>
                  <Textarea
                    id="comment"
                    placeholder="Provide any additional rejection comments..."
                    value={actionForm.comment}
                    onChange={(e) => setActionForm({ ...actionForm, comment: e.target.value })}
                    className="border-slate-200 min-h-[70px]"
                  />
                </div>
              </>
            )}

            {processingAction === 'TRANSFER' && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="utr" className="text-sm font-semibold">Transaction UTR / Reference Number *</Label>
                  <Input
                    id="utr"
                    placeholder="Enter IMPS/NEFT UTR reference ID"
                    value={actionForm.utr}
                    onChange={(e) => setActionForm({ ...actionForm, utr: e.target.value })}
                    className="border-slate-200 font-mono"
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="transferDate" className="text-sm font-semibold">Transfer Date *</Label>
                  <Input
                    id="transferDate"
                    type="date"
                    value={actionForm.transferDate}
                    onChange={(e) => setActionForm({ ...actionForm, transferDate: e.target.value })}
                    className="border-slate-200"
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="comment" className="text-sm font-semibold">Transfer Comments</Label>
                  <Textarea
                    id="comment"
                    placeholder="Provide any additional transfer comments..."
                    value={actionForm.comment}
                    onChange={(e) => setActionForm({ ...actionForm, comment: e.target.value })}
                    className="border-slate-200 min-h-[70px]"
                  />
                </div>
              </>
            )}

            <DialogFooter className="mt-4 flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setProcessingAction(null)} className="border-slate-200">Cancel</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold" disabled={loadingAction}>
                {loadingAction ? "Processing..." : "Submit Decision"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Dialog: View Full Withdrawal Request Audit Details ── */}
      <Dialog open={!!detailedRequest} onOpenChange={() => setDetailedRequest(null)}>
        <DialogContent className="max-w-lg bg-white rounded-xl shadow-lg border border-slate-100">
          <DialogHeader>
            <DialogTitle>Withdrawal Request Audit Log</DialogTitle>
            <DialogDescription>Full tracking details for transaction ID: {detailedRequest?.requestId}</DialogDescription>
          </DialogHeader>

          {detailedRequest && (
            <div className="space-y-5 pt-2">
              <div className="flex justify-between items-center border-b border-slate-50 pb-3 text-sm">
                <div>
                  <span className="text-xs text-slate-400 uppercase font-semibold">Status</span>
                  <div className="flex items-center gap-1 mt-1 font-bold">
                    <Badge className={`px-2.5 py-0.5 border text-xs font-semibold uppercase rounded-full ${STATUS_COLORS[detailedRequest.status] || ''}`}>
                      {detailedRequest.status}
                    </Badge>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-xs text-slate-400 uppercase font-semibold">Net Payout Amount</span>
                  <div className="text-lg font-bold text-indigo-600 mt-0.5">₹{detailedRequest.netTransferAmount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-xs">
                <div>
                  <span className="text-slate-400 font-medium">Merchant ID / Name</span>
                  <span className="font-bold text-gray-800 block mt-0.5">{detailedRequest.merchantName} ({detailedRequest.merchantId})</span>
                </div>
                <div>
                  <span className="text-slate-400 font-medium">Requested Amt / Charge</span>
                  <span className="font-semibold text-gray-850 block mt-0.5">₹{detailedRequest.amount?.toLocaleString()} (Chg: ₹{detailedRequest.charge || 0})</span>
                </div>
                <div>
                  <span className="text-slate-400 font-medium">Balance Before Request</span>
                  <span className="font-semibold text-gray-800 block mt-0.5">₹{detailedRequest.balanceBefore?.toLocaleString() || '—'}</span>
                </div>
                <div>
                  <span className="text-slate-400 font-medium">Balance After Request</span>
                  <span className="font-semibold text-gray-800 block mt-0.5">₹{detailedRequest.balanceAfter?.toLocaleString() || '—'}</span>
                </div>
                <div>
                  <span className="text-slate-400 font-medium">Bank Destination Info</span>
                  <span className="font-semibold text-gray-800 block mt-0.5">{detailedRequest.bankName} • A/C: {detailedRequest.accountNumber}</span>
                </div>
                <div>
                  <span className="text-slate-400 font-medium">Bank IFSC Code</span>
                  <span className="font-mono font-bold text-gray-800 block mt-0.5">{detailedRequest.ifscCode}</span>
                </div>
                <div>
                  <span className="text-slate-400 font-medium">UTR Reference / Date</span>
                  <span className="font-mono font-bold text-gray-800 block mt-0.5">{detailedRequest.utr || '—'} {detailedRequest.transferDate && `(${formatDateDDMMYYYY(detailedRequest.transferDate)})`}</span>
                </div>
                <div>
                  <span className="text-slate-400 font-medium">Created Timestamp</span>
                  <span className="font-semibold text-gray-800 block mt-0.5">{formatDateTimeDDMMYYYY(detailedRequest.createdAt)}</span>
                </div>
              </div>

              {detailedRequest.rejectionReason && (
                <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-red-800 text-xs">
                  <strong>Rejection Reason:</strong> {detailedRequest.rejectionReason}
                </div>
              )}

              {/* Status Audit History */}
              {detailedRequest.history && detailedRequest.history.length > 0 && (
                <div className="border-t border-slate-50 pt-4">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-3">Status Log History</span>
                  <div className="space-y-3 max-h-40 overflow-y-auto pr-1">
                    {detailedRequest.history.map((log, idx) => (
                      <div key={idx} className="flex gap-2 items-start text-xs border-l-2 border-slate-100 pl-3 ml-1">
                        <div className="min-w-0 flex-1">
                          <p className="text-gray-500">
                            Changed from <strong className="uppercase">{log.fromStatus || 'INITIATED'}</strong> to <strong className="uppercase">{log.toStatus}</strong>
                          </p>
                          {log.comment && <p className="text-gray-700 italic mt-0.5">"{log.comment}"</p>}
                          <p className="text-[10px] text-gray-400 mt-1">By {log.actor} · {formatDateTimeDDMMYYYY(log.createdAt)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Withdrawals;
